import { Link } from 'react-router-dom';

import { DocChronologyEmptyStateIllustration } from './doc-chronology-empty-state-illustration';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';

interface HowToCreateDocChronologyDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string;
  subsetId: string;
}

export function HowToCreateDocChronologyDialog({
  isOpen,
  onOpenChange,
  matterId,
  subsetId,
}: HowToCreateDocChronologyDialogProps) {
  const isUsingNewKim = localStorage.getItem('isUsingNewKim') === 'true';
  const kimPath = `/matters/${matterId}/subsets/${subsetId}/${isUsingNewKim ? 'new-kim' : 'kim'}`;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--folder-git-2" />
            How to Create a Document Chronology
          </DialogTitle>
          <DialogDescription>
            Choose the workflow that best matches how you want to build your document chronology.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-12 mb-8 flex w-full scale-110 justify-center">
          <DocChronologyEmptyStateIllustration />
        </div>

        <p className="mb-6 px-3">
          Document chronologies in Wexler let you build timelines from a collection of documents. These timelines can
          then be edited and exported.
        </p>

        <div className="space-y-3">
          <HowToCreateOption
            title="Ask Kim to identify documents"
            description="Ask Kim to find the specific documents you need, then export them straight into a document chronology."
            to={kimPath}
            cta="Open Kim"
            onOpenChange={onOpenChange}
          />
          <HowToCreateOption
            title="From the documents table"
            description="Select the documents you want, then add them to a new document chronology."
            to={`/matters/${matterId}/subsets/${subsetId}/docs`}
            cta="Open the documents table"
            onOpenChange={onOpenChange}
          />
          <HowToCreateOption
            title="From an existing chronology"
            description="Open a fact chronology and convert it into a document chronology."
            to={`/matters/${matterId}/subsets/${subsetId}/chronologies`}
            cta="Open chronologies"
            onOpenChange={onOpenChange}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HowToCreateOption({
  title,
  description,
  to,
  cta,
  onOpenChange,
}: {
  title: string;
  description: string;
  to: string;
  cta: string;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <div className="space-y-0.5">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="text-sm text-gray-600">{description}</p>
      </div>

      <Button asChild variant="white" size="sm" className="shrink-0">
        <Link to={to} onClick={() => onOpenChange(false)}>
          {cta}
          <Icon className="lucide--arrow-up-right" />
        </Link>
      </Button>
    </div>
  );
}
